import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cn } from "../../lib/utils";

interface ProgressProps
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  /** Optional label shown below/inside the bar for accessibility. */
  label?: string;
}

/**
 * Progress — Clones Design System.
 *
 * Per the handoff: a 2px-tall persona-coloured "sync line" with a 6px
 * persona dot trailing the leading edge. The value drives the indicator
 * width; the trailing dot is a ::after pseudo on the indicator. Indeterminate
 * mode (no value) animates a 4s "syncSweep" left-to-right that matches the
 * streaming-cursor cadence used by the chat composer.
 */
const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, label, ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    className={cn(
      "relative h-[2px] w-full overflow-visible rounded-none bg-[var(--line-strong)]",
      className,
    )}
    aria-label={label ?? "Прогресс"}
    {...props}
  >
    <ProgressPrimitive.Indicator
      className={cn(
        "relative h-full bg-[var(--persona)] transition-[width] duration-[var(--dur-3)] ease-[var(--ease-out)]",
        "after:content-[''] after:absolute after:right-0 after:top-[-2px] after:h-[6px] after:w-[6px] after:rounded-full after:bg-[var(--persona)]",
      )}
      style={{ width: `${value ?? 0}%` }}
    />
  </ProgressPrimitive.Root>
));
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
